// Milníky k oslavě — když hodnota překročí některý práh, zobrazí se animovaná oslava.
// Stav se ukládá do localStorage, aby se oslava za tentýž milník neopakovala.
import { fmt } from "./format.js";

// Konfigurace prahů pro každou sledovanou metriku.
// thresholds = pevné milníky; progressive = dále se přidávají po kroku `step`.
export const MILESTONES = {
  lifetimeRevenue: {
    label: "Lifetime obrat",
    thresholds: [10_000_000, 20_000_000, 50_000_000, 100_000_000, 1_000_000_000],
    formatValue: (v) => `${fmt(v)} Kč`,
    emoji: "💰",
    color: "from-emerald-500 to-green-600",
    message: (v) => `Tvůj lifetime obrat překonal ${fmt(v)} Kč!`,
  },
  servedClients: {
    label: "Obsloužených klientů",
    thresholds: [200, 500, 1000, 2000, 5000, 10000],
    formatValue: (v) => `${v}`,
    emoji: "👥",
    color: "from-blue-500 to-plum-600",
    message: (v) => `Obsloužil jsi už ${v} klientů!`,
  },
  activeClients: {
    label: "Aktuálně aktivních klientů",
    thresholds: [100, 200, 300, 500, 700, 1000],
    progressive: { after: 1000, step: 300 },
    formatValue: (v) => `${v}`,
    emoji: "🔥",
    color: "from-brand-500 to-pink-600",
    message: (v) => `Máš ${v} aktivních klientů! Tvá klientská základna roste.`,
  },
  mrr: {
    label: "MRR",
    thresholds: [500_000, 750_000, 1_000_000, 1_500_000, 2_000_000],
    progressive: { after: 2_000_000, step: 500_000 },
    formatValue: (v) => `${fmt(v)} Kč`,
    emoji: "📈",
    color: "from-emerald-500 to-teal-600",
    message: (v) => `MRR překonalo ${fmt(v)} Kč měsíčně!`,
  },
  daysInBusiness: {
    label: "Dní v podnikání",
    thresholds: [2000],
    progressive: { after: 2000, step: 1000 },
    formatValue: (v) => `${v}`,
    emoji: "🎖️",
    color: "from-amber-500 to-orange-600",
    message: (v) => `Podnikáš už ${v} dní! To je kus cesty.`,
  },
  totalTrainings: {
    label: "Tréninků celkem",
    thresholds: [10000],
    progressive: { after: 10000, step: 10000 },
    formatValue: (v) => `${fmt(v)}`,
    emoji: "💪",
    color: "from-rose-500 to-pink-600",
    message: (v) => `Tvůj tým odtrénoval už ${fmt(v)} tréninků!`,
  },
  totalTrials: {
    label: "Trialů celkem",
    thresholds: [300, 500, 750, 1000],
    progressive: { after: 1000, step: 1000 },
    formatValue: (v) => `${v}`,
    emoji: "🎯",
    color: "from-brand-500 to-brand-600",
    message: (v) => `Máš za sebou ${v} zkušebních tréninků!`,
  },
  avgDeal: {
    label: "Průměrný deal",
    thresholds: [7500, 10000],
    progressive: { after: 10000, step: 2500 },
    formatValue: (v) => `${fmt(v)} Kč`,
    emoji: "🏆",
    color: "from-amber-400 to-yellow-600",
    message: (v) => `Průměrná hodnota dealu překonala ${fmt(v)} Kč!`,
  },
};

// Vrátí seznam všech prahů, které platí pro danou metriku až do dané hodnoty.
function getAllThresholds(cfg, currentValue) {
  const all = [...cfg.thresholds];
  if (cfg.progressive) {
    const { after, step } = cfg.progressive;
    let t = after + step;
    while (t <= currentValue) {
      all.push(t);
      t += step;
    }
  }
  return all.sort((a, b) => a - b);
}

// Najde NEJNOVĚJŠÍ dosažený práh (pro aktuální hodnotu).
export function findLatestAchieved(cfg, currentValue) {
  const all = getAllThresholds(cfg, currentValue);
  return [...all].reverse().find((t) => currentValue >= t) || null;
}

// Klíč v localStorage pro konkrétní milník (metrika + práh).
export function milestoneKey(metricKey, threshold) {
  return `fw40_milestone_${metricKey}_${threshold}`;
}

// Projede všechny metriky a vrátí pole nově dosažených milníků
// (tj. těch, které ještě nejsou v localStorage).
export function detectNewMilestones(values) {
  const pending = [];
  for (const [key, cfg] of Object.entries(MILESTONES)) {
    const current = values[key];
    if (typeof current !== "number" || current <= 0) continue;
    const latest = findLatestAchieved(cfg, current);
    if (!latest) continue;
    const storageKey = milestoneKey(key, latest);
    try {
      if (!localStorage.getItem(storageKey)) {
        pending.push({ metricKey: key, cfg, threshold: latest, currentValue: current, storageKey });
      }
    } catch {
      // localStorage není dostupný — ignoruj
    }
  }
  return pending;
}

// Označí milník jako zobrazený (uloží timestamp do localStorage).
export function markMilestoneSeen(storageKey) {
  try {
    localStorage.setItem(storageKey, new Date().toISOString());
  } catch {
    // localStorage není dostupný — ignoruj
  }
}

// Reset pro testovací účely (smaže všechny milníky — budou se znovu zobrazovat).
export function resetAllMilestones() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("fw40_milestone_"))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignoruj
  }
}
